<template>
  <div class="location-selector">
    <div class="row">
      <b-form-group class="col-4">
        <template #label>
          <label>Thành phố:</label>
        </template>
        <b-form-select
          size="sm"
          v-model="localModel.city"
          :options="cities"
          class="select-style"
          value-field="id"
          text-field="name"
          :disabled="disabled"
          :state="cityState"
        >
          <template v-slot:first>
            <b-form-select-option :value="null"
              >-- Thành phố --</b-form-select-option
            >
          </template>
        </b-form-select>
      </b-form-group>

      <b-form-group class="col-4">
        <template #label>
          <label>Quận:</label>
        </template>
        <b-form-select
          size="sm"
          v-model="localModel.district"
          :options="districts"
          class="select-style"
          value-field="id"
          text-field="name"
          :disabled="disabled || !localModel.city || isLoadingDistricts"
          :state="districtState"
        >
          <template v-slot:first>
            <b-form-select-option :value="null">
              <span v-if="isLoadingDistricts">Đang tải...</span>
              <span v-else>-- Quận --</span>
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <b-form-group class="col-4">
        <template #label>
          <label>Phường:</label>
        </template>
        <b-form-select
          size="sm"
          v-model="localModel.ward"
          :options="wards"
          class="select-style"
          value-field="id"
          text-field="name"
          :disabled="disabled || !localModel.district || isLoadingWards"
          :state="wardState"
        >
          <template v-slot:first>
            <b-form-select-option :value="null">
              <span v-if="isLoadingWards">Đang tải...</span>
              <span v-else>-- Phường --</span>
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, computed, watch, ref } from 'vue';
import useLocationHierarchy from '../composables/useLocationHierarchy';

export default defineComponent({
  name: 'LocationSelector',

  props: {
    value: {
      type: Object,
      default: () => ({ city: null, district: null, ward: null }),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input'],

  setup(props, { emit }) {
    const localModel = reactive({
      city: props.value.city,
      district: props.value.district,
      ward: props.value.ward,
    });

    const {
      cities,
      districts,
      wards,
      loadDistricts,
      loadWards,
      isLoadingCities,
      isLoadingDistricts,
      isLoadingWards,
    } = useLocationHierarchy();

    watch(
      () => props.value,
      (newValue) => {
        if (localModel.city !== newValue.city) {
          localModel.city = newValue.city;

          if (newValue.city) {
            loadDistricts(newValue.city, false);
          }
        }

        if (localModel.district !== newValue.district) {
          localModel.district = newValue.district;

          if (newValue.district) {
            loadWards(newValue.district, false);
          }
        }

        if (localModel.ward !== newValue.ward) {
          localModel.ward = newValue.ward;
        }
      },
      { deep: true },
    );

    watch(
      localModel,
      (newValue) => {
        emit('input', {
          city: newValue.city,
          district: newValue.district,
          ward: newValue.ward,
        });
      },
      { deep: true },
    );

    const isUserInitiatedChange = ref(false);

    watch(
      () => localModel.city,
      (newCityId, oldCityId) => {
        if (!newCityId) return;

        loadDistricts(newCityId);

        const isFirstChange = oldCityId === undefined;
        const shouldResetDependentFields =
          isUserInitiatedChange.value &&
          !isFirstChange &&
          !props.value.district;

        if (shouldResetDependentFields) {
          localModel.district = null;
          localModel.ward = null;
        }

        if (!isFirstChange) {
          isUserInitiatedChange.value = true;
        }
      },
    );

    watch(
      () => localModel.district,
      (newDistrictId, oldDistrictId) => {
        if (!newDistrictId) return;

        loadWards(newDistrictId);

        const isFirstChange = oldDistrictId === undefined;
        const shouldResetWard =
          isUserInitiatedChange.value && !isFirstChange && !props.value.ward;

        if (shouldResetWard) {
          localModel.ward = null;
        }
      },
    );

    const cityState = computed(() => {
      if (!props.validate || !props.required) return null;
      return localModel.city ? true : false;
    });

    const districtState = computed(() => {
      if (!props.validate || !props.required) return null;
      return localModel.district ? true : false;
    });

    const wardState = computed(() => {
      if (!props.validate || !props.required) return null;
      return localModel.ward ? true : false;
    });

    return {
      localModel,
      cities,
      districts,
      wards,
      isLoadingCities,
      isLoadingDistricts,
      isLoadingWards,
      cityState,
      districtState,
      wardState,
    };
  },
});
</script>

<style scoped>
.select-style {
  border-radius: 0.28rem;
  box-shadow: none;
  border: 1px solid #ced4da;
}
</style>
